import { Box, Button, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useMemo, useState } from 'react'

import { EmptyState as SharedEmptyState } from '../../components/EmptyState'
import { getTranslate } from '../../localization'

/**
 * Upper bound on retained rows across pages.
 *
 * 200 preserves the bound `appendBoundedResults` applied. It moves here from a
 * screen-local helper because the shared `useCursorPage` takes it as
 * `maxRetained` — the helper and the hook did the same slice, and only one of
 * them is the app's.
 */
export const RETAINED_RESULTS = 200

/**
 * Kept for PreferencesPanel, which fetches one page of preferences to render a
 * form rather than a paginated list. The list panels no longer use it —
 * `useCursorPage` owns pageSize/pageToken for those.
 */
export const pageVariables = { pageSize: 20 }
export const t = getTranslate
export const idempotencyKey = () => {
  const bytes = new Uint32Array(4)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => value.toString(16)).join('-')
}

export const ErrorState = ({
  message,
  onRetry
}: {
  message: string
  onRetry?: () => void
}) => (
  <Alert severity="error" role="alert">
    {t(message)}
    {onRetry && <Button onClick={onRetry}>{t('Retry')}</Button>}
  </Alert>
)

// EmptyState now delegates to the shared No-Data.png placeholder. Kept as a
// named alias so the ~10 call sites below read unchanged; the rendering moved,
// the contract did not.
export const EmptyState = SharedEmptyState

export function LoadMoreButton({
  onLoadMore
}: {
  onLoadMore: () => Promise<unknown>
}) {
  const [loadingMore, setLoadingMore] = useState(false)
  const [failed, setFailed] = useState(false)
  return (
    <Box>
      <Button
        disabled={loadingMore}
        onClick={async () => {
          setLoadingMore(true)
          setFailed(false)
          try {
            await onLoadMore()
          } catch (loadError) {
            setFailed(true)
          } finally {
            setLoadingMore(false)
          }
        }}>
        {loadingMore ? t('Loading more…') : t('Load more')}
      </Button>
      {failed && (
        <Typography role="alert">
          {t('Unable to load more results.')}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Every primitive leaf of a row, lowercased, for client-side matching.
 *
 * Not the rendered columns: `ResourceColumn.render` returns ReactNode, so
 * matching its output would mean rendering to a string. Not JSON.stringify
 * either — that folds in keys and punctuation, so a search for "key" would
 * match every row through the field NAME rather than any value.
 *
 * Recurses so nested values are searchable — a category's displayName.en is
 * the label an operator actually reads, and it is not a top-level field.
 *
 * Assumes JSON-shaped values, which is what these queries return. A class
 * instance whose own properties are non-enumerable — a `Date`, say — yields
 * `[]` from Object.values and silently stops being searchable, which reads as
 * a missing row rather than as an error. Latent today: every timestamp on
 * these resources arrives as an ISO string.
 */
export function searchableText(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map(searchableText).join(' ')
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map(searchableText)
      .join(' ')
  }
  return String(value).toLowerCase()
}

/**
 * Narrows rows to those matching `search`.
 *
 * Filters what is LOADED, not what exists. These lists are cursor-paginated
 * and the server takes no filter argument, so a search that finds nothing
 * means "not on the pages fetched so far" — which is why the count beside the
 * input says "loaded" and never "total".
 */
export function useFilteredRows<T>(rows: T[], search: string): T[] {
  // Normalizing every nested value is the expensive part of filtering. The
  // loaded-row snapshot changes only after a query/refetch/fetchMore, whereas
  // the search changes on every keystroke, so build the index once per
  // snapshot and reuse it while the operator types.
  const indexedRows = useMemo(
    () => rows.map((row) => ({ row, text: searchableText(row) })),
    [rows]
  )
  const query = search.trim().toLowerCase()

  return useMemo(() => {
    if (!query) return rows
    return indexedRows
      .filter(({ text }) => text.includes(query))
      .map(({ row }) => row)
  }, [indexedRows, query, rows])
}
